import { Box, IconButton } from '@mui/material';
import {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import * as repertoireActions from '../../Actions/repertoire';
import { RepertoireReducer } from 'Types/RepertoireTypes';

import { createContext, useContext } from 'react';

const PreventRepClickContext = createContext(false);
// eslint-disable-next-line react-refresh/only-export-components -- usePreventRepClick is a hook, not a component
export const usePreventRepClick = () => useContext(PreventRepClickContext);

interface DragScrollProps {
  children: ReactNode;
  rowId: string;
}

export const DragScroll: React.FC<DragScrollProps> = ({ children, rowId }) => {
  const dispatch = useDispatch();
  const rawScrollPositions = useSelector(
    (state: RepertoireReducer) => state.repertoireReducer.horizontalScrollPositions,
  );
  const horizontalScrollPositions = useMemo(() => rawScrollPositions ?? {}, [rawScrollPositions]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [preventRepClick, setPreventRepClick] = useState(false);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const scrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preventClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRestoredScroll = useRef(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;
    setIsDragging(true);
    setStartX(e.clientX);
    setScrollLeft(container.scrollLeft);
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (isDragging && container) {
      const dx = startX - e.clientX;
      if (Math.abs(dx) > 5) {
        // Drag threshold of 5 pixels
        setPreventRepClick(true);
        container.scrollLeft = scrollLeft + dx;
      }
    }
  };
  const handleMouseUp = () => {
    setIsDragging(false);
    if (preventClickTimeoutRef.current) clearTimeout(preventClickTimeoutRef.current);
    preventClickTimeoutRef.current = setTimeout(() => {
      setPreventRepClick(false);
      preventClickTimeoutRef.current = null;
    }, 250);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const handleDragStart = (e: DragEvent) => e.preventDefault();
      container.addEventListener('dragstart', handleDragStart);
      return () => {
        container.removeEventListener('dragstart', handleDragStart);
      };
    }
  }, []);

  const checkOverflow = () => {
    if (containerRef.current) {
      const isOverflowing = containerRef.current.scrollWidth > containerRef.current.clientWidth;
      setHasOverflow(isOverflowing);
      setAtStart(containerRef.current.scrollLeft <= 0);
      const isAtEnd =
        containerRef.current.scrollLeft + containerRef.current.clientWidth >=
        containerRef.current.scrollWidth;
      setAtEnd(isAtEnd);
    }
  };

  // Debounced save of horizontal scroll position to Redux (only fires 150ms after scrolling stops)
  const debouncedSaveScrollPosition = useMemo(
    () =>
      debounce((position: number) => {
        dispatch(repertoireActions.setHorizontalScroll({ rowId, position }));
      }, 150),
    [dispatch, rowId],
  );

  // Flush pending scroll position save on unmount (ensures position is saved before navigating away)
  useEffect(() => {
    return () => debouncedSaveScrollPosition.flush();
  }, [debouncedSaveScrollPosition]);

  // Restore horizontal scroll position on mount (useLayoutEffect to prevent flash)
  useLayoutEffect(() => {
    if (containerRef.current && rowId && !hasRestoredScroll.current) {
      const savedPosition = horizontalScrollPositions[rowId];
      if (savedPosition && savedPosition > 0) {
        containerRef.current.scrollLeft = savedPosition;
        hasRestoredScroll.current = true;
      }
    }
  }, [rowId, horizontalScrollPositions]);

  // Attach scroll listener - re-attaches if debouncedSaveScrollPosition changes (e.g., rowId changes)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setAtStart(container.scrollLeft <= 0);
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth;
      setAtEnd(isAtEnd);
      // Save scroll position to Redux (debounced)
      debouncedSaveScrollPosition(container.scrollLeft);
    };

    checkOverflow();
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [debouncedSaveScrollPosition]);

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const scrollLeftClick = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollByHold = useCallback((delta: number) => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: delta, behavior: 'auto' });
    }
  }, []);

  const stopScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    // needed so when holding scroll and it reaches the end, it will stop
    if (atEnd) stopScroll();
  }, [atEnd, stopScroll]);

  useEffect(() => {
    // needed so when holding scroll and it reaches the start, it will stop
    if (atStart) stopScroll();
  }, [atStart, stopScroll]);

  const startScroll = useCallback(
    (delta: number) => {
      stopScroll();
      scrollTimeoutRef.current = setTimeout(() => {
        scrollTimeoutRef.current = null;
        scrollIntervalRef.current = setInterval(() => scrollByHold(delta), 10);
      }, 200);
    },
    [scrollByHold, stopScroll],
  );

  const startScrollRight = () => startScroll(10);
  const startScrollLeft = () => startScroll(-10);

  useEffect(
    () => () => {
      stopScroll();
      if (preventClickTimeoutRef.current) clearTimeout(preventClickTimeoutRef.current);
    },
    [stopScroll],
  );

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      <PreventRepClickContext.Provider value={preventRepClick}>
        <Box
          sx={{ display: 'flex', gap: '14px', overflowX: 'auto' }}
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {children}
        </Box>
      </PreventRepClickContext.Provider>

      {hasOverflow && !atStart && (
        <IconButton
          sx={leftArrowButtonStyles}
          onClick={scrollLeftClick}
          onMouseDown={startScrollLeft}
          onMouseUp={stopScroll}
          onMouseLeave={stopScroll}
        >
          <ArrowBack sx={{ color: '#ECECEC' }} />
        </IconButton>
      )}

      {hasOverflow && !atEnd && (
        <IconButton
          sx={rightArrowButtonStyles}
          onClick={scrollRight}
          onMouseDown={startScrollRight}
          onMouseUp={stopScroll}
          onMouseLeave={stopScroll}
        >
          <ArrowForward sx={{ color: '#ECECEC' }} />
        </IconButton>
      )}
    </Box>
  );
};

const arrowButtonStyles = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(23, 31, 49, 0.8)',
  '&:hover': {
    backgroundColor: 'rgba(23, 31, 49, 1)',
  },
  zIndex: 1,
};

const rightArrowButtonStyles = {
  ...arrowButtonStyles,
  right: 0,
};

const leftArrowButtonStyles = {
  ...arrowButtonStyles,
  left: 0,
};
